import React from 'react';

import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
// eslint-disable-next-line no-restricted-imports
import ReactLoadable from 'react-loadable';

import { fg } from '@townsquare/stat-sig/gate';

// Migrating off react-loadable: its loader-resolve setState early-returns
// when `_mounted` is false, which fires under React 18 Suspense bail-out
// and leaves slots stuck in the loading branch. React.lazy has no such race.

type Loader<Exports> = () => Promise<Exports>;
type LoadingComponent = React.ComponentType<{
	isLoading: boolean;
	pastDelay: boolean;
	timedOut: boolean;
	error: unknown;
	retry: () => void;
}>;

// Renders the UFO hold wrapper around the optional loading UI in one place
// rather than via a factory function, so both branches can use it directly.
const UFOLoading = ({
	Loading,
	...props
}: { Loading: LoadingComponent | undefined } & React.ComponentProps<LoadingComponent>) => (
	<UFOLoadHold name="loadable-hold">{Loading ? <Loading {...props} /> : null}</UFOLoadHold>
);

function noop() {}

type LoadableErrorBoundaryProps = {
	LoadingComp: LoadingComponent;
	onRetry: () => void;
	children: React.ReactNode;
};

type LoadableErrorBoundaryState = {
	error: unknown;
};

// Bridges React.lazy's error semantics back to the LoadingComponent
// `error` prop that consumers of @townsquare/loadable rely on. Without
// this, a loader rejection would propagate as an uncaught exception
// instead of rendering an in-place error state.
class LoadableErrorBoundary extends React.Component<
	LoadableErrorBoundaryProps,
	LoadableErrorBoundaryState
> {
	state: LoadableErrorBoundaryState = { error: null };

	static getDerivedStateFromError(error: unknown): LoadableErrorBoundaryState {
		return { error };
	}

	private retry = () => {
		// React.lazy caches the rejection in its internal payload and won't
		// re-invoke the loader on a plain re-render. The owning component
		// owns the lazy instance; bump its version via onRetry so a fresh
		// React.lazy is constructed (and the loader runs again).
		this.props.onRetry();
		this.setState({ error: null });
	};

	render() {
		const { error } = this.state;
		const { LoadingComp, children } = this.props;
		if (error !== null) {
			return (
				<LoadingComp
					isLoading={false}
					pastDelay={false}
					timedOut={false}
					error={error}
					retry={this.retry}
				/>
			);
		}
		return children;
	}
}

// `fg()` may throw if Statsig is not yet initialised when a Loadable HOC
// is constructed at module-evaluation time. Treat any failure as
// "gate off" so we never break module evaluation on a feature-flag lookup.
function isLazyGateEnabled(): boolean {
	try {
		return fg('townsquare_loadable_use_react_lazy');
	} catch {
		return false;
	}
}

// Self-contained component encapsulating the retry/Suspense/error-boundary
// wiring for the React.lazy path. Keeping it here makes buildLazyLoadable
// a thin setup function rather than a mix of setup + render logic.
function RetryableLazy<Props>({
	loadModule,
	LoadingComp,
	cachedPromiseRef,
	...props
}: {
	loadModule: () => Promise<{ default: React.ComponentType<Props> }>;
	LoadingComp: LoadingComponent;
	cachedPromiseRef: { current: Promise<{ default: React.ComponentType<Props> }> | null };
} & Props) {
	const [version, setVersion] = React.useState(0);
	const Lazy = React.useMemo(
		() => React.lazy(loadModule) as unknown as React.ComponentType<object>,
		// version intentionally drives the re-creation
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[version],
	);
	const onRetry = React.useCallback(() => {
		// Clear the wrapper's promise cache too so we don't hand back the
		// rejected one to the freshly-created React.lazy.
		cachedPromiseRef.current = null;
		setVersion((v) => v + 1);
	}, [cachedPromiseRef]);

	return (
		<LoadableErrorBoundary LoadingComp={LoadingComp} onRetry={onRetry}>
			<React.Suspense
				fallback={
					<UFOLoading
						Loading={LoadingComp}
						isLoading
						pastDelay={false}
						timedOut={false}
						error={null}
						retry={noop}
					/>
				}
			>
				<Lazy {...(props as object)} />
			</React.Suspense>
		</LoadableErrorBoundary>
	);
}

function buildLazyLoadable<Props, Exports extends object>(
	options: ReactLoadable.Options<Props, Exports>,
): Branch<Props> {
	const loader = options.loader as Loader<Exports>;
	const LoadingComp = options.loading as LoadingComponent | undefined;
	const customRender = (
		options as unknown as { render?: (loaded: Exports, props: Props) => React.ReactNode }
	).render;

	// Memoise the loader promise so .preload() and the first render share
	// it. Clear on rejection so the error-boundary retry path can re-load.
	const cachedPromiseRef: { current: Promise<{ default: React.ComponentType<Props> }> | null } = {
		current: null,
	};
	const loadModule = () => {
		if (!cachedPromiseRef.current) {
			cachedPromiseRef.current = loader()
				.then((loaded) => {
					if (customRender) {
						// react-loadable's `render` API picks a non-default export;
						// wrap it so React.lazy gets the `{ default }` shape it requires.
						const Rendered: React.ComponentType<Props> = (props) => (
							<>{customRender(loaded, props)}</>
						);
						return { default: Rendered };
					}
					const Component =
						(loaded as { default?: React.ComponentType<Props> })?.default ??
						(loaded as unknown as React.ComponentType<Props>);
					return { default: Component };
				})
				.catch((error) => {
					cachedPromiseRef.current = null;
					throw error;
				});
		}
		return cachedPromiseRef.current;
	};

	const LazyComponent: React.ComponentType<Props> = (props) => (
		<RetryableLazy
			loadModule={loadModule}
			LoadingComp={LoadingComp ?? (() => null)}
			cachedPromiseRef={cachedPromiseRef}
			{...props}
		/>
	);

	return { Component: LazyComponent, preload: loadModule };
}

type Branch<Props> = { Component: React.ComponentType<Props>; preload: () => Promise<unknown> };

function buildReactLoadable<Props, Exports extends object>(
	options: ReactLoadable.Options<Props, Exports>,
): Branch<Props> {
	// Don't mutate the caller's options object — create a copy with the
	// UFO-wrapped loading component. (This also removes the implicit ordering
	// dependency between buildLazyLoadable and buildReactLoadable.)
	const WrappedLoading: LoadingComponent = (props) => (
		<UFOLoading Loading={options.loading as LoadingComponent | undefined} {...props} />
	);
	const LoadableComponent = ReactLoadable({
		...options,
		loading: WrappedLoading,
	} as ReactLoadable.Options<Props, Exports>);
	return {
		Component: LoadableComponent as unknown as React.ComponentType<Props>,
		preload: () => Promise.resolve(LoadableComponent.preload()),
	};
}

const Loadable = <Props, Exports extends object>(
	options: ReactLoadable.Options<Props, Exports>,
): ReturnType<typeof ReactLoadable<Props, Exports>> => {
	// Defer construction to first use (render or preload) so fg() is never
	// called at module-evaluation time, when Statsig may not yet be initialised.
	// Statsig is sticky per session, so the branch never flips mid-lifecycle.
	let built: Branch<Props> | null = null;
	const getBranch = (): Branch<Props> => {
		if (!built) {
			built = isLazyGateEnabled()
				? buildLazyLoadable<Props, Exports>(options)
				: buildReactLoadable<Props, Exports>(options);
		}
		return built;
	};

	const GatedLoadable = (props: Props) => {
		const { Component } = getBranch();
		const C = Component as React.ComponentType<object>;
		return <C {...(props as object)} />;
	};

	// Give the wrapper a discoverable name in DevTools / error stacks.
	// Derive from the loader function name where possible (rsbuild / webpack
	// produce names like `LoadableComponent_loader__WEBPACK_IMPORTED_MODULE_…`
	// — fall back to plain "Loadable" if nothing useful is exposed).
	const loaderName = (options.loader as { name?: string } | undefined)?.name;
	GatedLoadable.displayName = loaderName ? `Loadable(${loaderName})` : 'Loadable';

	// Preload dispatches on the gate state at call time. In a staged rollout
	// the gate is sticky per session (Statsig assigns once at boot), so a
	// prefetch and the subsequent render always agree on which branch to warm.
	(GatedLoadable as { preload?: () => Promise<unknown> }).preload = () => getBranch().preload();

	return GatedLoadable as any as ReturnType<typeof ReactLoadable<Props, Exports>>;
};

export default Loadable;
